import { apiClient } from '@/lib/apiClient';
import type { AutocompleteCategoryResult, AutocompleteResponse, AutocompleteResult } from '@/types';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAutocompleteOptions {
    minChars?: number;
    debounceMs?: number;
    maxCacheSize?: number;
    scope?: 'all' | 'classified' | 'listing';
}

interface UseAutocompleteReturn {
    results: AutocompleteResult[];
    categories: AutocompleteCategoryResult[];
    total: number;
    loading: boolean;
    error: string | null;
    search: (query: string) => void;
    clearResults: () => void;
}

export function useAutocomplete(options: UseAutocompleteOptions = {}): UseAutocompleteReturn {
    const { minChars = 3, debounceMs = 300, maxCacheSize = 50, scope = 'all' } = options;

    const [results, setResults] = useState<AutocompleteResult[]>([]);
    const [categories, setCategories] = useState<AutocompleteCategoryResult[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const abortControllerRef = useRef<AbortController | null>(null);
    const debounceTimerRef = useRef<number | null>(null);
    const cacheRef = useRef<Map<string, AutocompleteResponse>>(new Map());

    const fetchAutocomplete = useCallback(
        async (query: string) => {
            const normalizedQuery = query.trim().toLowerCase();

            const cached = cacheRef.current.get(normalizedQuery);
            if (cached) {
                setResults(cached.results);
                setCategories(cached.categories || []);
                setTotal(cached.total);
                setLoading(false);
                return;
            }

            // Cancel previous request if it exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            const currentController = abortControllerRef.current;

            try {
                setLoading(true);
                setError(null);

                const response = await apiClient.get<AutocompleteResponse>('/api/classifieds/autocomplete', {
                    params: { q: query, scope },
                    signal: currentController.signal,
                });

                const data = response.data;

                if (cacheRef.current.size >= maxCacheSize) {
                    const firstKey = cacheRef.current.keys().next().value;
                    if (firstKey) {
                        cacheRef.current.delete(firstKey);
                    }
                }
                cacheRef.current.set(normalizedQuery, data);

                setResults(data.results);
                setCategories(data.categories || []);
                setTotal(data.total);
            } catch (err: any) {
                if (err.name !== 'CanceledError') {
                    console.error('Autocomplete error:', err);
                    setError((err?.message as string) || 'Failed to fetch results');
                    setResults([]);
                    setTotal(0);
                }
            } finally {
                if (abortControllerRef.current === currentController) {
                    setLoading(false);
                }
            }
        },
        [maxCacheSize, scope],
    );

    const search = useCallback(
        (query: string) => {
            if (debounceTimerRef.current) {
                window.clearTimeout(debounceTimerRef.current);
            }

            if (query.length < minChars) {
                setResults([]);
                setCategories([]);
                setTotal(0);
                setLoading(false);
                setError(null);
                return;
            }

            setLoading(true);

            debounceTimerRef.current = window.setTimeout(() => {
                fetchAutocomplete(query);
            }, debounceMs);
        },
        [minChars, debounceMs, fetchAutocomplete],
    );

    const clearResults = useCallback(() => {
        setResults([]);
        setCategories([]);
        setTotal(0);
        setLoading(false);
        setError(null);
    }, []);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (debounceTimerRef.current) {
                window.clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return {
        results,
        categories,
        total,
        loading,
        error,
        search,
        clearResults,
    };
}
